/**
 * Colour helpers shared by the API (achievement contrast checks) and the UI
 * (Space themes, contrast warnings, theme import).
 *
 * Everything here works on `#RRGGBB` hex strings. Output is always lowercase.
 *
 * @module utils/color
 */
/** Strict `#RRGGBB` (case-insensitive). */
export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
/** Whether `value` is a strict `#RRGGBB` hex colour. */
export function isHexColor(value) {
    return typeof value === 'string' && HEX_COLOR_PATTERN.test(value);
}
function parseHexChannels(hex) {
    const raw = hex.replace(/^#/, '');
    return {
        r: parseInt(raw.substring(0, 2), 16),
        g: parseInt(raw.substring(2, 4), 16),
        b: parseInt(raw.substring(4, 6), 16),
    };
}
function toHexByte(n) {
    const clamped = Math.max(0, Math.min(255, Math.round(n)));
    return clamped.toString(16).padStart(2, '0');
}
function rgbToHex({ r, g, b }) {
    return `#${toHexByte(r)}${toHexByte(g)}${toHexByte(b)}`;
}
function linearise(channel) {
    const s = channel / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}
/**
 * WCAG 2.x relative luminance of a `#RRGGBB` colour (leading `#` optional).
 * Returns a value between 0 (black) and 1 (white).
 */
export function relativeLuminance(hex) {
    const { r, g, b } = parseHexChannels(hex);
    return 0.2126 * linearise(r) + 0.7152 * linearise(g) + 0.0722 * linearise(b);
}
/**
 * WCAG 2.x contrast ratio between two `#RRGGBB` hex colours.
 * Returns a value between 1 (identical) and 21 (black vs white).
 */
export function contrastRatio(hexA, hexB) {
    const lA = relativeLuminance(hexA);
    const lB = relativeLuminance(hexB);
    const lighter = Math.max(lA, lB);
    const darker = Math.min(lA, lB);
    return (lighter + 0.05) / (darker + 0.05);
}
/** Linear blend of two `#RRGGBB` colours. `t` is clamped to [0, 1]; 0 gives `a`. */
export function mixHex(a, b, t) {
    const weight = Number.isFinite(t) ? Math.max(0, Math.min(1, t)) : 0;
    const ca = parseHexChannels(a);
    const cb = parseHexChannels(b);
    return rgbToHex({
        r: ca.r + (cb.r - ca.r) * weight,
        g: ca.g + (cb.g - ca.g) * weight,
        b: ca.b + (cb.b - ca.b) * weight,
    });
}
/** True when a colour reads as dark (white text would contrast better than black). */
export function isDarkHex(hex) {
    return contrastRatio(hex, '#ffffff') >= contrastRatio(hex, '#000000');
}
/**
 * Move a colour toward its contrasting extreme (lighter for dark colours,
 * darker for light ones). Used to derive hover/active/muted shades.
 */
export function shadeHex(hex, t) {
    return mixHex(hex, isDarkHex(hex) ? '#ffffff' : '#000000', t);
}
/** `#RRGGBB` → `"R, G, B"` for CSS variables that only accept triplets. */
export function hexToRgbTriplet(hex) {
    const { r, g, b } = parseHexChannels(hex);
    return `${r}, ${g}, ${b}`;
}
/** `#RRGGBB` + alpha (0–1) → `#rrggbbaa`. */
export function hexWithAlpha(hex, alpha) {
    const a = Number.isFinite(alpha) ? Math.max(0, Math.min(1, alpha)) : 1;
    return `${rgbToHex(parseHexChannels(hex))}${toHexByte(a * 255)}`;
}
function hslToRgb(h, s, l) {
    const hue = ((h % 360) + 360) % 360;
    const sat = Math.max(0, Math.min(1, s));
    const light = Math.max(0, Math.min(1, l));
    const c = (1 - Math.abs(2 * light - 1)) * sat;
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
    const m = light - c / 2;
    let rgb;
    if (hue < 60)
        rgb = [c, x, 0];
    else if (hue < 120)
        rgb = [x, c, 0];
    else if (hue < 180)
        rgb = [0, c, x];
    else if (hue < 240)
        rgb = [0, x, c];
    else if (hue < 300)
        rgb = [x, 0, c];
    else
        rgb = [c, 0, x];
    return { r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 };
}
function parseCssNumber(part, percentScale) {
    const trimmed = part.trim();
    if (trimmed.endsWith('%')) {
        const n = Number(trimmed.slice(0, -1));
        return Number.isFinite(n) ? (n / 100) * percentScale : null;
    }
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
}
/**
 * Best-effort conversion of a CSS colour string to `#rrggbb`.
 *
 * Handles `#rgb`, `#rrggbb`, `#rrggbbaa` (alpha dropped), `rgb()/rgba()`, and
 * `hsl()/hsla()`. Anything else (named colours, `var()`, `oklch`) returns null
 * so callers can fall back instead of guessing.
 */
export function cssColorToHex(value) {
    if (typeof value !== 'string')
        return null;
    const v = value.trim().toLowerCase();
    const hexMatch = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(v);
    if (hexMatch) {
        const raw = hexMatch[1];
        if (raw.length === 3) {
            return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`;
        }
        return `#${raw.slice(0, 6)}`;
    }
    const fn = /^(rgba?|hsla?)\(([^)]*)\)$/.exec(v);
    if (!fn)
        return null;
    const parts = fn[2].split(/[\s,/]+/).filter(Boolean);
    if (parts.length < 3)
        return null;
    if (fn[1].startsWith('rgb')) {
        const channels = parts.slice(0, 3).map((p) => parseCssNumber(p, 255));
        if (channels.some((c) => c === null))
            return null;
        const [r, g, b] = channels;
        return rgbToHex({ r: r, g: g, b: b });
    }
    const h = Number(parts[0].replace(/deg$/, ''));
    const s = parseCssNumber(parts[1], 1);
    const l = parseCssNumber(parts[2], 1);
    if (!Number.isFinite(h) || s === null || l === null)
        return null;
    return rgbToHex(hslToRgb(h, s, l));
}
/**
 * Map a full UI theme onto the smaller Space theme token set.
 *
 * Used for "Start from" presets (built-in, saved, and community themes).
 * Tokens whose source value is not a parseable colour are omitted, so the
 * Space falls back to the viewer's own theme for that slot.
 */
export function deriveSpaceThemeFromUiTheme(colors) {
    const pick = (value) => cssColorToHex(value) ?? undefined;
    const out = {
        background: pick(colors.bgPrimary),
        sidebar: pick(colors.bgSecondary),
        card: pick(colors.bgTertiary),
        elevated: pick(colors.bgElevated),
        text: pick(colors.textPrimary),
        textMuted: pick(colors.textSecondary),
        accent: pick(colors.accentPrimary),
        accentText: pick(colors.textInverse),
        link: pick(colors.accentPrimary),
        border: pick(colors.border),
    };
    for (const key of Object.keys(out)) {
        if (out[key] === undefined)
            delete out[key];
    }
    return out;
}
//# sourceMappingURL=color.js.map